import React from 'react'
import clsx from 'clsx'
import { ArrowDownRight, ArrowUpRight, Info, Minus, type LucideIcon } from 'lucide-react'
import { computeDelta } from '@/lib/dashboardFormat'
import { useTranslation } from 'react-i18next'

export type MetricAccent = 'blue' | 'green' | 'red' | 'purple' | 'orange' | 'slate'

const accentStyles: Record<MetricAccent, { chip: string; icon: string; value: string }> = {
  blue: {
    chip: 'bg-blue-50 dark:bg-blue-500/15',
    icon: 'text-[#2563EB]',
    value: 'text-foreground',
  },
  green: {
    chip: 'bg-green-50 dark:bg-green-500/15',
    icon: 'text-[#16A34A]',
    value: 'text-foreground',
  },
  red: {
    chip: 'bg-red-50 dark:bg-red-500/15',
    icon: 'text-[#DC2626]',
    value: 'text-foreground',
  },
  purple: {
    chip: 'bg-purple-50 dark:bg-purple-500/15',
    icon: 'text-[#9333EA]',
    value: 'text-foreground',
  },
  orange: {
    chip: 'bg-orange-50 dark:bg-orange-500/15',
    icon: 'text-[#EA580C]',
    value: 'text-foreground',
  },
  slate: {
    chip: 'bg-card-muted',
    icon: 'text-muted-foreground',
    value: 'text-foreground',
  },
}

const toneStyles = {
  positive: 'text-[#16A34A] bg-green-50 dark:bg-green-500/15',
  negative: 'text-[#DC2626] bg-red-50 dark:bg-red-500/15',
  neutral: 'text-muted-foreground bg-card-muted',
} as const

interface MetricCardProps {
  label: string
  value: string
  icon: LucideIcon
  accent?: MetricAccent
  /** Short line under the label explaining what the metric means */
  description?: string
  /** Breakdown under the value, e.g. "18 ganhos de 100 leads recebidos" */
  detail?: string
  /** Current and previous raw numbers drive the variation badge */
  current?: number
  previous?: number
  comparisonLabel?: string
  /** For metrics where going down is good, such as losses */
  lowerIsBetter?: boolean
  /** Longer explanation shown in the info tooltip next to the title (detailed layout) */
  helper?: string
  loading?: boolean
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon: Icon,
  accent = 'blue',
  description,
  detail,
  current,
  previous,
  comparisonLabel,
  lowerIsBetter = false,
  helper,
  loading = false,
}) => {
  const { t } = useTranslation()
  const style = accentStyles[accent]
  const hasComparison = current !== undefined && previous !== undefined
  const delta = hasComparison ? computeDelta(current, previous, lowerIsBetter) : null
  const detailed = Boolean(description)

  if (loading) {
    return (
      <div className="min-h-[200px] rounded-[20px] border border-border bg-card px-5 py-5 shadow-sm">
        <div className="animate-pulse">
          <div className="mb-4 h-10 w-10 rounded-full bg-card-muted" />
          <div className="mb-2 h-4 w-3/4 rounded bg-card-muted" />
          <div className="mb-3 h-3 w-full rounded bg-card-muted" />
          <div className="mb-2 h-8 w-1/3 rounded bg-card-muted" />
          <div className="h-3 w-2/3 rounded bg-card-muted" />
        </div>
      </div>
    )
  }

  const ToneIcon =
    delta?.tone === 'positive' ? ArrowUpRight : delta?.tone === 'negative' ? ArrowDownRight : Minus

  const comparisonBlock = delta ? (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className={clsx(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold',
          toneStyles[delta.tone],
        )}
      >
        <ToneIcon className="h-3 w-3" strokeWidth={2.5} />
        {delta.label}
      </span>
      {comparisonLabel && (
        <span className="text-[11px] text-muted-foreground">{comparisonLabel}</span>
      )}
    </div>
  ) : null

  if (detailed) {
    return (
      <div
        className={clsx(
          'flex min-h-[260px] flex-col rounded-[20px] border border-border bg-card px-6 py-6 shadow-sm',
          'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
        )}
      >
        <div className="flex flex-1 flex-col">
          <div className="flex items-start gap-3">
            <div
              className={clsx(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                style.chip,
              )}
            >
              <Icon className={clsx('h-5 w-5', style.icon)} strokeWidth={2} />
            </div>

            <div className="flex min-h-[4.25rem] min-w-0 flex-1 flex-col">
              <div className="flex items-center gap-1.5">
                <p className="text-[14px] font-semibold leading-tight text-foreground">
                  {label}
                </p>
                {helper && (
                  <span className="group relative shrink-0">
                    <button
                      type="button"
                      className="inline-flex text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={t("common:dashboard.aboutPrefix", { label })}
                    >
                      <Info className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                    <span
                      role="tooltip"
                      className={clsx(
                        'pointer-events-none invisible absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-xl',
                        'border border-border bg-card px-3 py-2 text-[11px] leading-relaxed text-muted-foreground shadow-md',
                        'opacity-0 transition-opacity duration-150',
                        'group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100',
                      )}
                    >
                      {helper}
                    </span>
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[12px] leading-tight text-muted-foreground">
                {description}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-1.5">
            <p
              className={clsx(
                'text-[32px] font-bold leading-none tracking-tight',
                style.value,
              )}
            >
              {value}
            </p>
            <p className="min-h-[1.125rem] text-[12px] leading-tight text-muted-foreground">
              {detail || '\u00a0'}
            </p>
          </div>
        </div>

        <div className="mt-5 border-t border-border pt-3 min-h-[28px]">
          {comparisonBlock}
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'flex min-h-[152px] flex-col justify-between rounded-[20px] border border-border bg-card px-5 py-5 shadow-sm',
        'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
      )}
    >
      <div className="flex flex-col gap-3">
        <div className={clsx('flex h-10 w-10 items-center justify-center rounded-full', style.chip)}>
          <Icon className={clsx('h-5 w-5', style.icon)} strokeWidth={2} />
        </div>
        <div className="flex flex-col gap-1">
          <p
            className={clsx(
              'text-[28px] font-bold leading-none tracking-tight',
              style.value,
            )}
          >
            {value}
          </p>
          <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-1">
        {comparisonBlock}
        {helper && <p className="text-[11px] leading-snug text-muted-foreground">{helper}</p>}
      </div>
    </div>
  )
}

export default MetricCard
